import React, { useEffect, useRef, useState } from "react";
import api, { fileUrl, uploadImage } from "@/lib/api";
import { PageHeader, Card, Empty, Button, Modal, Input, Select, Field } from "@/components/ui-kit";
import { useAuth } from "@/context/AuthContext";
import { Phone, Plus, Pencil, Trash2, UserPlus, Network, Camera, Loader2, ChevronUp, ChevronDown, X } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  { key: "president", label: "Président" },
  { key: "president_honneur", label: "Président d'Honneur" },
  { key: "parrain_marraine", label: "Parrain & Marraine du Club" },
  { key: "conseiller", label: "Conseiller Club" },
  { key: "tresorier", label: "Trésorier du Club" },
  { key: "secretaire", label: "Secrétaire Club" },
  { key: "comite_parents", label: "Comité des Parents" },
  { key: "coach", label: "Coach" },
];
const emptyForm = { role: "president", name: "", phone: "", photo: "" };

export default function Staff() {
  const { hasRole } = useAuth();
  const isAdmin = hasRole("admin");
  const [entries, setEntries] = useState([]);
  const [customRoles, setCustomRoles] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/org-chart").then((r) => setEntries(r.data || [])).catch(() => {});
  const loadRoles = () => api.get("/org-roles").then((r) => setCustomRoles(r.data || [])).catch(() => {});
  useEffect(() => { load(); loadRoles(); }, []);

  const allRoles = [...ROLES, ...customRoles.map((r) => ({ key: r.key, label: r.label, id: r.id, custom: true }))];

  const openNew = (role) => { setEditId(null); setForm({ ...emptyForm, role: role || allRoles[0].key }); setModal(true); };
  const openEdit = (e) => { setEditId(e.id); setForm({ role: e.role, name: e.name, phone: e.phone || "", photo: e.photo || "" }); setModal(true); };

  const onPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try { const path = await uploadImage(file); setForm((f) => ({ ...f, photo: path })); }
    catch { toast.error("Échec du téléchargement de la photo"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Le nom est obligatoire"); return; }
    setSaving(true);
    try {
      if (editId) await api.put(`/org-chart/${editId}`, form);
      else await api.post("/org-chart", form);
      toast.success("Organigramme mis à jour");
      setModal(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const remove = async (e) => {
    if (!window.confirm(`Retirer ${e.name} de l'organigramme ?`)) return;
    await api.delete(`/org-chart/${e.id}`); toast.success("Retiré"); load();
  };

  const move = async (e, direction) => {
    await api.post(`/org-chart/${e.id}/move`, { direction }).catch(() => {});
    load();
  };

  const addRole = async () => {
    const label = window.prompt("Nom du nouveau rôle (ex. Responsable matériel) :");
    if (!label || !label.trim()) return;
    try { await api.post("/org-roles", { label: label.trim() }); toast.success("Rôle ajouté"); loadRoles(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const removeRole = async (roleId) => {
    if (!window.confirm("Supprimer ce rôle personnalisé et les personnes associées ?")) return;
    await api.delete(`/org-roles/${roleId}`); toast.success("Rôle supprimé"); loadRoles(); load();
  };

  const hasAny = entries.length > 0;

  return (
    <div>
      <PageHeader
        title="Organigramme"
        subtitle="La structure et les responsables du club."
        action={isAdmin ? (
          <Button data-testid="org-add-btn" onClick={() => openNew()}><Plus size={16} /> Ajouter</Button>
        ) : null}
      />

      {!hasAny && !isAdmin && <Empty text="L'organigramme n'est pas encore disponible." />}

      <div className="max-w-5xl mx-auto">
        {allRoles.map((role, idx) => {
          const people = entries.filter((e) => e.role === role.key);
          if (!people.length && !isAdmin) return null;
          return (
            <div key={role.key} data-testid={`org-role-${role.key}`}>
              {idx > 0 && <div className="flex justify-center"><div className="w-px h-6 sm:h-8 bg-border" /></div>}
              <div className="flex flex-col items-center">
                <div className="flex items-center gap-1.5 mb-4">
                  <div className="px-4 py-1.5 rounded-full bg-primary/15 text-primary font-display font-bold uppercase tracking-tight text-xs sm:text-sm text-center">
                    {role.label}
                  </div>
                  {isAdmin && role.custom && (
                    <button data-testid={`org-del-role-${role.id}`} onClick={() => removeRole(role.id)}
                      title="Supprimer ce rôle" className="p-1 rounded-full text-muted-foreground hover:text-destructive"><X size={14} /></button>
                  )}
                </div>
                {people.length ? (
                  <div className="flex flex-wrap justify-center gap-3 sm:gap-4 w-full">
                    {people.map((p, pi) => (
                      <div key={p.id} data-testid="org-person-card"
                        className="relative group w-[46%] sm:w-44 bg-card border border-border rounded-2xl p-3 sm:p-4 flex flex-col items-center text-center transition-colors hover:border-primary/40">
                        {p.photo ? (
                          <img src={fileUrl(p.photo)} alt={p.name} className="w-14 h-14 rounded-full object-cover border border-border" />
                        ) : (
                          <div className="w-14 h-14 rounded-full bg-primary/15 flex items-center justify-center text-primary font-black text-lg">
                            {p.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                          </div>
                        )}
                        <p className="font-bold text-sm mt-2 leading-tight">{p.name}</p>
                        {p.phone && (
                          <a href={`tel:${p.phone}`} data-testid="org-person-phone"
                            className="text-xs text-muted-foreground mt-1 flex items-center gap-1 hover:text-primary transition-colors">
                            <Phone size={12} /> {p.phone}
                          </a>
                        )}
                        {isAdmin && people.length > 1 && (
                          <div className="flex items-center gap-1 mt-2">
                            <button data-testid={`org-up-${p.id}`} onClick={() => move(p, "up")} disabled={pi === 0}
                              title="Monter" className="p-1 rounded-md bg-secondary text-muted-foreground hover:text-foreground disabled:opacity-30"><ChevronUp size={14} /></button>
                            <button data-testid={`org-down-${p.id}`} onClick={() => move(p, "down")} disabled={pi === people.length - 1}
                              title="Descendre" className="p-1 rounded-md bg-secondary text-muted-foreground hover:text-foreground disabled:opacity-30"><ChevronDown size={14} /></button>
                          </div>
                        )}
                        {isAdmin && (
                          <div className="absolute top-1.5 right-1.5 flex gap-1 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                            <button data-testid={`org-edit-${p.id}`} onClick={() => openEdit(p)} className="p-1.5 rounded-lg bg-secondary text-muted-foreground hover:text-foreground"><Pencil size={13} /></button>
                            <button data-testid={`org-del-${p.id}`} onClick={() => remove(p)} className="p-1.5 rounded-lg bg-secondary text-muted-foreground hover:text-destructive"><Trash2 size={13} /></button>
                          </div>
                        )}
                      </div>
                    ))}
                    {isAdmin && (
                      <button data-testid={`org-add-to-${role.key}`} onClick={() => openNew(role.key)}
                        className="w-[46%] sm:w-44 min-h-[110px] rounded-2xl border border-dashed border-border text-muted-foreground hover:border-primary/50 hover:text-primary transition-colors flex flex-col items-center justify-center gap-1 text-xs font-semibold">
                        <UserPlus size={18} /> Ajouter
                      </button>
                    )}
                  </div>
                ) : (
                  isAdmin && (
                    <button data-testid={`org-add-to-${role.key}`} onClick={() => openNew(role.key)}
                      className="w-44 min-h-[90px] rounded-2xl border border-dashed border-border text-muted-foreground hover:border-primary/50 hover:text-primary transition-colors flex flex-col items-center justify-center gap-1 text-xs font-semibold">
                      <UserPlus size={18} /> Assigner
                    </button>
                  )
                )}
              </div>
            </div>
          );
        })}
        {isAdmin && (
          <div className="flex justify-center mt-8">
            <button data-testid="org-add-role-btn" onClick={addRole}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-dashed border-border text-muted-foreground hover:border-primary/50 hover:text-primary transition-colors text-sm font-semibold">
              <Plus size={16} /> Nouveau rôle
            </button>
          </div>
        )}
      </div>

      <Modal open={modal} onClose={() => setModal(false)} title={editId ? "Modifier l'entrée" : "Ajouter à l'organigramme"}>
        <div className="space-y-4">
          <Field label="Rôle">
            <Select data-testid="org-role-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
              {allRoles.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
            </Select>
          </Field>
          <Field label="Nom de la personne">
            <Input data-testid="org-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex. Jean Dupont" />
          </Field>
          <Field label="Téléphone (optionnel)">
            <Input data-testid="org-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Ex. +32 ..." />
          </Field>
          <Field label="Photo (optionnel)">
            <div className="flex items-center gap-3">
              {form.photo ? (
                <img src={fileUrl(form.photo)} alt="" className="w-12 h-12 rounded-full object-cover border border-border" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center text-muted-foreground"><Camera size={18} /></div>
              )}
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => onPhoto(e.target.files?.[0])} />
              <Button variant="outline" type="button" onClick={() => fileRef.current?.click()} disabled={uploading}>
                {uploading ? <Loader2 size={15} className="animate-spin" /> : <Camera size={15} />} {form.photo ? "Changer" : "Ajouter une photo"}
              </Button>
            </div>
          </Field>
          <div className="flex gap-2 pt-2">
            <Button data-testid="org-save-btn" onClick={save} disabled={saving} className="flex-1">{saving ? "..." : "Enregistrer"}</Button>
            <Button variant="outline" type="button" onClick={() => setModal(false)}>Annuler</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
